import constants from '@playcastdotio/GlobalConfig';
import type { PlayerCampaignTheme, FlowGraph } from '@playcastdotio/Messaging';

type CampaignIdsResponse = {
  success: boolean;
  campaignIds?: string[];
  error?: string;
};

type PlayerThemeResponse = {
  success: boolean;
  data?: unknown;
  error?: string;
};

const unwrapPlayerTheme = (data: unknown): PlayerCampaignTheme | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const record = data as Record<string, unknown>;
  const nestedTheme = record.theme;
  if (nestedTheme && typeof nestedTheme === 'object') {
    const nestedRecord = nestedTheme as Record<string, unknown>;
    if (nestedRecord.theme && typeof nestedRecord.theme === 'object') {
      return nestedRecord.theme as PlayerCampaignTheme;
    }
    return nestedTheme as PlayerCampaignTheme;
  }

  return record as PlayerCampaignTheme;
};

const ensureTrailingSlash = (url: string) => (url.endsWith('/') ? url : `${url}/`);

const fetchJson = async <T>(url: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<T>;
};

const isActiveTheme = (theme: PlayerCampaignTheme | null): boolean => {
  if (theme && typeof (theme as Record<string, unknown>).active === 'boolean') {
    return Boolean((theme as Record<string, unknown>).active);
  }
  // If the API didn't include active, trust the IDs endpoint.
  return Boolean(theme);
};

export const fetchFlowGraph = async (campaignId: string): Promise<FlowGraph | null> => {
  const baseUrl = ensureTrailingSlash(constants.REALTIME_API_BASE_URL);
  try {
    const response = await fetchJson<{ success: boolean; data: FlowGraph; isDefault?: boolean }>(
      `${baseUrl}realtime/campaigns/${campaignId}/flow-graph`
    );
    if (response?.isDefault) return null;
    return response?.data ?? null;
  } catch {
    return null;
  }
};

export const fetchActiveCampaignThemes = async (): Promise<PlayerCampaignTheme[]> => {
  const baseUrl = ensureTrailingSlash(constants.REALTIME_API_BASE_URL);

  try {
    const idsResponse = await fetchJson<CampaignIdsResponse>(`${baseUrl}realtime/campaigns/ids`);
    const campaignIds = idsResponse?.campaignIds ?? [];
    if (!idsResponse?.success || campaignIds.length === 0) {
      if (!idsResponse?.success) {
        console.warn('[Campaigns] Failed to load campaign IDs', idsResponse?.error);
      } else {
        console.warn('[Campaigns] No campaign IDs returned from API');
      }
      return [];
    }

    const themes = await Promise.all(
      campaignIds.map(async (campaignId) =>
        fetchJson<PlayerThemeResponse>(`${baseUrl}realtime/campaigns/${campaignId}/player-theme`)
          .then((response) => unwrapPlayerTheme(response?.data))
          .catch((error) => {
            console.warn('[Campaigns] Failed to load theme', campaignId, error);
            return null;
          }),
      ),
    );

    return themes.filter((theme): theme is PlayerCampaignTheme => isActiveTheme(theme));
  } catch (error) {
    console.error('[Campaigns] Failed to load active campaigns', error);
    return [];
  }
};
